import moment from "moment";
import _ from "lodash";
import { useCombineOrigin } from "./ReportingOverviewHooks";

export const usePinkButtonClickData = (pinkButtonClicks) => {
  if (!pinkButtonClicks.length) return [{ count: 0, duration: 0 }, {}];
  if (pinkButtonClicks.length === 1) return [pinkButtonClicks[0], {}];

  return pinkButtonClicks;
};

const useTotalViewsData = (videoReport, dateRange) => {
  const viewRecords = videoReport.totalViews;

  const startDate = dateRange.start;
  const endDate = dateRange.end;

  const numberOfDays = endDate.diff(startDate, "days");
  const numberOfMonths =
    endDate.startOf("month").diff(startDate.startOf("month"), "months") + 1;
  let totalViewsGraphData;

  if (numberOfMonths >= 3) {
    const months = Array(numberOfMonths)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "months"))
      .map((date) => ({ date, viewCount: 0 }));

    viewRecords.forEach((viewRecord) => {
      const matchingDate = months.findIndex((day) => {
        const viewDate = moment(viewRecord.date);
        return viewDate.isSame(day.date, "month");
      });

      if (months[matchingDate]) {
        if (months[matchingDate].viewCount) {
          months[matchingDate].viewCount += viewRecord.viewCount;
        } else {
          months[matchingDate].viewCount = viewRecord.viewCount;
        }
      } else {
        months[matchingDate] = {
          ...viewRecord,
          date: viewRecord.date
        };
      }
    });

    totalViewsGraphData = months.map((m) => ({
      ...m,
      date: m.date.unix() * 1000
    }));
  } else {
    const days = Array(numberOfDays)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "days"))
      .map((date) => ({ date, viewCount: 0 }));

    viewRecords.forEach((viewRecord) => {
      const matchingDate = days.findIndex((day) => {
        const viewDate = moment(viewRecord.date);
        viewDate.isSame(day.date, "day");
      });
      days[matchingDate] = {
        ...viewRecord,
        date: days[matchingDate] ? days[matchingDate].date : viewRecord.date
      };
    });

    totalViewsGraphData = days.map((d) => ({
      ...d,
      date: d.date.unix() * 1000
    }));
  }

  return {
    totalViewsGraphData
  };
};

export const useAggregateGraphData = (graphDataKey, records, dateRange) => {
  const startDate = dateRange.start;
  const endDate = dateRange.end;

  const numberOfDays = endDate.diff(startDate, "days");
  const numberOfMonths =
    endDate.startOf("month").diff(startDate.startOf("month"), "months") + 1;
  let aggregatedGraphData;

  if (numberOfMonths >= 3) {
    const months = Array(numberOfMonths)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "months"))
      .map((date) => ({ date, [graphDataKey]: 0 }));

    records.forEach((record) => {
      const matchingDate = months.findIndex((day) =>
        moment(record.date).isSame(day.date, "month")
      );
      if (months[matchingDate]) {
        if (months[matchingDate][graphDataKey]) {
          months[matchingDate][graphDataKey] += record[graphDataKey];
        } else {
          months[matchingDate][graphDataKey] = record[graphDataKey];
        }
      } else {
        months[matchingDate] = {
          ...record,
          date: record.date
        };
      }
    });

    aggregatedGraphData = months.map((m) => ({
      ...m,
      date: m.date.unix() * 1000
    }));
  } else {
    const days = Array(numberOfDays)
      .fill(0)
      .map((value, index) => moment(startDate).add(index, "days"))
      .map((date) => ({ date, [graphDataKey]: 0 }));

    records.forEach((record) => {
      const matchingDate = days.findIndex((day) => {
        const saleDate = moment(record.date);
        saleDate.isSame(day.date, "day");
      });
      days[matchingDate] = {
        ...record,
        date: days[matchingDate] ? days[matchingDate].date : record.date
      };
    });

    aggregatedGraphData = days.map((d) => ({
      ...d,
      date: d.date.unix() * 1000
    }));
  }

  return aggregatedGraphData;
};

const useReportingVideo = (videoReport, dateRange) => {
  const { totalViewsGraphData } = useTotalViewsData(videoReport, {
    start: moment(dateRange[0]),
    end: moment(dateRange[1])
  });

  const totalSalesGraphData = useAggregateGraphData(
    "salesAmount",
    videoReport.totalSales,
    {
      start: moment(dateRange[0]),
      end: moment(dateRange[1])
    }
  );

  const pinkButtonClicks = usePinkButtonClickData(videoReport.pinkButtonClicks);

  const viewDurationGraphData = videoReport.averageDurationOfViews.length
    ? videoReport.averageDurationOfViews
    : [{ viewerCount: 0, duration: 0 }];

  const videoOriginList = videoReport.originList.map((origin) => ({
    id: origin.origin,
    origin: origin.origin,
    views: origin.totalViews
  }));

  const videoOriginListTableData = useCombineOrigin(videoOriginList);

  return {
    totalViewsGraphData,
    totalViewsColor: "#ff7373",
    viewDurationGraphData,
    viewDurationColor: "#4a90e2",
    pinkButtonClicks,
    pinkButtonColor: "#ac7df2",
    totalSalesGraphData,
    totalSalesColor: "#65cfa1",
    videoOriginListTableData
  };
};

export default useReportingVideo;
